import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useNotifications } from '../../contexts/NotificationContext'
import { useHaptics } from '../../utils/haptics'
import { useMoney } from '../../hooks/usePublicSettings'
import { jobsApi, statusLabel } from '../../utils/accountApi'

// Icons
function ArrowLeftIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M19 12H5M12 19l-7-7 7-7"/>
    </svg>
  )
}

function CalendarIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <path d="M16 2v4M8 2v4M3 10h18"/>
    </svg>
  )
}

function ClockIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 6v6l4 2"/>
    </svg>
  )
}

function MapPinIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  )
}

function PhoneIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  )
}

function MessageCircleIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    </svg>
  )
}

function CheckCircleIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <path d="M9 11l3 3L22 4"/>
    </svg>
  )
}

function AlertTriangleIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  )
}

function DownloadIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7,10 12,15 17,10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  )
}

function CameraIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
    </svg>
  )
}

function NavigationIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polygon points="3,11 22,2 13,21 11,13 3,11"/>
    </svg>
  )
}

function PlayIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polygon points="5,3 19,12 5,21"/>
    </svg>
  )
}

function PauseIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="6" y="4" width="4" height="16"/>
      <rect x="14" y="4" width="4" height="16"/>
    </svg>
  )
}

function UserCheckIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
      <path d="M16 11l2 2 4-4"/>
    </svg>
  )
}

const LIVE = ['EN_ROUTE', 'ON_SITE', 'IN_PROGRESS', 'PAUSED', 'AWAITING_RETURN']
const REFRESH_MS = 30000 // how often an active job is re-read while the screen is open
const ISSUE_TYPES = [['QUALITY', 'Quality of work'], ['PRICING', 'Price'], ['TIMELINE', 'Timing / delays'], ['SCOPE', 'Scope of work'], ['PAYMENT', 'Payment']]

const LIVE_INFO = {
  EN_ROUTE: { text: 'Professional is on the way', color: 'text-blue-600', bgColor: 'bg-blue-100', Icon: NavigationIcon },
  ON_SITE: { text: 'Professional has arrived', color: 'text-green-600', bgColor: 'bg-green-100', Icon: UserCheckIcon },
  IN_PROGRESS: { text: 'Work in progress', color: 'text-orange-600', bgColor: 'bg-orange-100', Icon: PlayIcon },
  PAUSED: { text: 'Work paused', color: 'text-yellow-600', bgColor: 'bg-yellow-100', Icon: PauseIcon },
  AWAITING_RETURN: { text: 'Visit finished, work continues on the next visit', color: 'text-slate-600', bgColor: 'bg-slate-100', Icon: ClockIcon }
}

export default function JobDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { showError, showSuccess } = useNotifications()
  const haptics = useHaptics()
  const money = useMoney()

  const [job, setJob] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [activeTab, setActiveTab] = useState('details') // 'details', 'progress', 'photos', 'invoice'
  const [loadedAt, setLoadedAt] = useState(Date.now())
  const [now, setNow] = useState(Date.now())
  const [busy, setBusy] = useState(null)
  const [signOff, setSignOff] = useState({ rating: 0, comment: '' })
  const [issueOpen, setIssueOpen] = useState(false)
  const [issue, setIssue] = useState({ type: 'QUALITY', description: '' })

  const loadJobDetails = useCallback(async () => {
    try {
      setJob(await jobsApi.job(id))
      setLoadedAt(Date.now())
      setLoadError(null)
    } catch (error) {
      setLoadError(error.message)
      setJob(null)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { loadJobDetails() }, [loadJobDetails])

  const live = job && LIVE.includes(job.job_status)
  const timerRunning = job?.ticket?.timer?.state === 'running'

  // Re-read an active job from the backend; tick the displayed work time between reads
  useEffect(() => {
    if (!live) return
    const poll = setInterval(loadJobDetails, REFRESH_MS)
    const tick = timerRunning ? setInterval(() => setNow(Date.now()), 1000) : null
    return () => { clearInterval(poll); if (tick) clearInterval(tick) }
  }, [live, timerRunning, loadJobDetails])

  const workSeconds = job?.ticket?.timer
    ? job.ticket.timer.seconds + (timerRunning ? Math.max(0, Math.floor((now - loadedAt) / 1000)) : 0)
    : 0

  const formatElapsedTime = (seconds) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    if (hours > 0) return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }

  const run = async (key, fn, ok) => {
    setBusy(key)
    haptics.light()
    try {
      await fn()
      haptics.success()
      showSuccess(ok)
      await loadJobDetails()
      return true
    } catch (e) {
      haptics.error()
      showError(e.message)
      return false
    } finally {
      setBusy(null)
    }
  }

  const phone = job?.professional?.phone
  const handleCall = () => { haptics.light(); if (phone) window.location.href = `tel:${phone}` }
  const handleMessage = () => { haptics.light(); if (phone) window.location.href = `sms:${phone}` }

  const submitIssue = (e) => {
    e.preventDefault()
    if (!issue.description.trim()) return showError('Please describe the problem')
    run('issue', () => jobsApi.reportIssue(job.job_id, issue), 'Issue reported. Our team will follow up.')
      .then(done => { if (done) { setIssueOpen(false); setIssue({ type: 'QUALITY', description: '' }) } })
  }

  const getStatusColor = (status) => {
    if (['COMPLETED', 'APPROVED'].includes(status)) return 'bg-green-100 text-green-800'
    if (status === 'CANCELLED' || status === 'DISPUTED') return 'bg-red-100 text-red-800'
    if (LIVE.includes(status)) return 'bg-blue-100 text-blue-800'
    return 'bg-yellow-100 text-yellow-800'
  }

  const formatDateTime = (d) => d ? new Date(d).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    )
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-slate-900 mb-2">Job not found</h2>
          {loadError && <p className="text-sm text-slate-500 mb-2">{loadError}</p>}
          <button onClick={() => navigate('/orders')} className="text-slate-600 hover:text-slate-900">
            Back to Orders
          </button>
        </div>
      </div>
    )
  }

  const info = LIVE_INFO[job.job_status]
  const openQuote = job.quotes.find(q => q.status === 'sent')
  const priceQuote = job.quotes.find(q => q.status === 'accepted') || openQuote || job.quotes[0]
  const openVariations = job.variations.filter(v => v.status === 'proposed')
  const canSignOff = job.job_status === 'COMPLETED' && !job.signoff
  const steps = job.ticket?.steps || []
  const stepsDone = steps.filter(s => s.status === 'done' || s.finished_at).length

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white px-4 py-4 shadow-lg">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-xl touch-manipulation">
            <ArrowLeftIcon />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-slate-900">{job.title}</h1>
            {job.job_number && <p className="text-sm text-slate-500">Job #{job.job_number}</p>}
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(job.job_status)}`}>
            {statusLabel(job.job_status)}
          </span>
        </div>
      </div>

      {/* Live job status (from the professional's job ticket) */}
      {info && (
        <div className="px-4 py-3 bg-slate-50">
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <info.Icon className={info.color} />
                <div>
                  <div className={`font-medium ${info.color}`}>{info.text}</div>
                  <div className="text-sm text-slate-600">
                    {job.professional?.name}{phone ? ` • ${phone}` : ''}
                  </div>
                </div>
              </div>
              {job.ticket?.on_site > 0 && (
                <div className={`px-3 py-1 rounded-full text-xs font-medium ${info.bgColor} ${info.color}`}>
                  {job.ticket.on_site} on site
                </div>
              )}
            </div>

            {(workSeconds > 0 || timerRunning) && (
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2">
                  <ClockIcon className="text-slate-600" />
                  <span className="text-sm font-medium text-slate-900">Work time{timerRunning ? '' : ' (paused)'}</span>
                </div>
                <div className="text-lg font-mono font-bold text-slate-900">{formatElapsedTime(workSeconds)}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Decisions waiting for the customer */}
      {(openQuote || openVariations.length > 0 || canSignOff) && (
        <div className="px-4 pt-3 space-y-3">
          {openQuote && (
            <div className="bg-white rounded-2xl p-4 shadow-lg border border-blue-100">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-slate-900">Quote waiting for you</div>
                <div className="font-bold text-slate-900">{money(openQuote.total)}</div>
              </div>
              {openQuote.valid_until && <div className="text-xs text-slate-500 mb-3">Valid until {formatDateTime(openQuote.valid_until)}</div>}
              <div className="flex gap-2">
                <button onClick={() => setActiveTab('invoice')} className="flex-1 py-2 border border-slate-300 rounded-xl text-sm font-medium">See breakdown</button>
                <button disabled={busy === 'quote'} onClick={() => run('quote', () => jobsApi.acceptQuote(openQuote.id), 'Quote accepted')}
                  className="flex-1 py-2 bg-slate-900 text-white rounded-xl text-sm font-medium disabled:opacity-50">
                  {busy === 'quote' ? 'Accepting…' : 'Accept quote'}
                </button>
              </div>
            </div>
          )}
          {openVariations.map(v => (
            <div key={v.id} className="bg-white rounded-2xl p-4 shadow-lg border border-yellow-100">
              <div className="flex items-center justify-between mb-1">
                <div className="font-semibold text-slate-900">Change to the work</div>
                {v.total != null && <div className="font-bold text-slate-900">{money(v.total)}</div>}
              </div>
              <div className="text-sm text-slate-600 mb-3">{v.reason}</div>
              <div className="flex gap-2">
                <button disabled={busy === v.id} onClick={() => run(v.id, () => jobsApi.decideVariation(v.id, false), 'Change rejected')}
                  className="flex-1 py-2 border border-slate-300 rounded-xl text-sm font-medium disabled:opacity-50">Reject</button>
                <button disabled={busy === v.id} onClick={() => run(v.id, () => jobsApi.decideVariation(v.id, true), 'Change approved')}
                  className="flex-1 py-2 bg-slate-900 text-white rounded-xl text-sm font-medium disabled:opacity-50">Approve</button>
              </div>
            </div>
          ))}
          {canSignOff && (
            <div className="bg-white rounded-2xl p-4 shadow-lg border border-green-100">
              <div className="font-semibold text-slate-900 mb-1">Work completed — sign off</div>
              <div className="text-sm text-slate-600 mb-3">Check the work and sign off if you are satisfied. Otherwise, report an issue.</div>
              <div className="flex gap-1 mb-3">
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} onClick={() => setSignOff({ ...signOff, rating: n })}
                    className={`text-2xl ${n <= signOff.rating ? 'text-yellow-400' : 'text-slate-300'}`}>★</button>
                ))}
              </div>
              <textarea rows={2} placeholder="Comment (optional)" value={signOff.comment}
                onChange={e => setSignOff({ ...signOff, comment: e.target.value })}
                className="w-full border border-slate-300 rounded-xl p-3 text-sm mb-3" />
              <button disabled={busy === 'signoff'}
                onClick={() => run('signoff', () => jobsApi.signOff(job.job_id, { rating: signOff.rating || null, comment: signOff.comment || null }), 'Job signed off')}
                className="w-full py-3 bg-slate-900 text-white rounded-xl font-medium disabled:opacity-50">
                {busy === 'signoff' ? 'Signing off…' : 'Approve & sign off'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="px-4 py-2">
        <div className="flex bg-slate-100 rounded-xl p-1">
          {[
            { id: 'details', label: 'Details' },
            { id: 'progress', label: 'Progress' },
            { id: 'photos', label: 'Photos' },
            { id: 'invoice', label: 'Price' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors touch-manipulation ${
                activeTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 py-4 space-y-6 pb-24">
        {/* Details Tab */}
        {activeTab === 'details' && (
          <>
            <div className="bg-white rounded-2xl p-5 shadow-lg">
              <h3 className="font-semibold text-slate-900 mb-3">Professional</h3>
              {job.professional ? (
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-slate-600">
                      {(job.professional.name || '?').split(' ').map(n => n[0]).slice(0, 2).join('')}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">{job.professional.name}</div>
                    {job.professional.contact && <div className="text-sm text-slate-600">{job.professional.contact}</div>}
                    <div className="text-sm text-slate-500">
                      {job.professional.reviews > 0 ? `Rating: ${job.professional.rating}/5 (${job.professional.reviews} reviews)` : 'No reviews yet'}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={handleCall} disabled={!phone} title={phone ? 'Call' : 'No phone number on file'}
                      className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 touch-manipulation disabled:opacity-40">
                      <PhoneIcon />
                    </button>
                    <button onClick={handleMessage} disabled={!phone} title={phone ? 'Text message' : 'No phone number on file'}
                      className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 touch-manipulation disabled:opacity-40">
                      <MessageCircleIcon />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-500">No professional assigned yet.</div>
              )}
            </div>

            <div className="bg-white rounded-2xl p-5 shadow-lg">
              <h3 className="font-semibold text-slate-900 mb-3">Job Details</h3>
              <div className="space-y-3">
                <div>
                  <div className="text-sm text-slate-500">Service Description</div>
                  <div className="font-medium text-slate-900">{job.description || '—'}</div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-slate-500">Scheduled</div>
                    <div className="font-medium text-slate-900 flex items-center gap-1">
                      <CalendarIcon />
                      {job.scheduled_start ? formatDateTime(job.scheduled_start) : 'To be confirmed'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">Urgency</div>
                    <div className="font-medium text-slate-900 flex items-center gap-1">
                      <ClockIcon />
                      {job.urgency ? job.urgency.toLowerCase() : '—'}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-slate-500">Service Address</div>
                  <div className="font-medium text-slate-900 flex items-start gap-1">
                    <MapPinIcon className="mt-0.5" />
                    <div>{job.place || '—'}</div>
                  </div>
                </div>
              </div>
            </div>

            {job.issues.length > 0 && (
              <div className="bg-white rounded-2xl p-5 shadow-lg">
                <h3 className="font-semibold text-slate-900 mb-3">Reported Issues</h3>
                <div className="space-y-2">
                  {job.issues.map(i => (
                    <div key={i.id} className="text-sm">
                      <div className="flex justify-between"><span className="font-medium">{i.dispute_type.toLowerCase()}</span><span className="text-xs text-slate-500">{i.status.toLowerCase().replace('_', ' ')}</span></div>
                      <div className="text-slate-600">{i.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Progress Tab */}
        {activeTab === 'progress' && (
          <>
            {steps.length > 0 && (
              <div className="bg-white rounded-2xl p-5 shadow-lg">
                <h3 className="font-semibold text-slate-900 mb-3">Work Steps</h3>
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600">Steps finished</span>
                    <span className="font-medium text-slate-900">{stepsDone} of {steps.length}</span>
                  </div>
                  <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-slate-900 transition-all duration-300 rounded-full" style={{ width: `${Math.round(stepsDone / steps.length * 100)}%` }} />
                  </div>
                </div>
                <div className="space-y-3">
                  {steps.map(s => {
                    const finished = s.status === 'done' || !!s.finished_at
                    return (
                      <div key={s.step_no} className="flex items-start gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center mt-0.5 ${finished ? 'bg-green-100 text-green-600' : s.started_at ? 'bg-orange-100 text-orange-600' : 'bg-slate-100 text-slate-400'}`}>
                          {finished ? <CheckCircleIcon className="w-4 h-4" /> : <div className="w-2 h-2 bg-current rounded-full" />}
                        </div>
                        <div className="flex-1">
                          <div className={`font-medium ${finished ? 'text-slate-900' : 'text-slate-500'}`}>{s.activity}</div>
                          <div className="text-xs text-slate-500">
                            {s.status === 'skipped' ? 'Not needed' : finished ? `Finished ${formatDateTime(s.finished_at)}` : s.started_at ? `Started ${formatDateTime(s.started_at)}` : 'Not started'}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            <div className="bg-white rounded-2xl p-5 shadow-lg">
              <h3 className="font-semibold text-slate-900 mb-4">Timeline</h3>
              <div className="space-y-4">
                {job.timeline.map((e, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center mt-0.5 bg-green-100 text-green-600">
                      <CheckCircleIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-slate-900">{statusLabel(e.status)}</div>
                      <div className="text-xs text-slate-500">{formatDateTime(e.at)}{e.note ? ` · ${e.note}` : ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Photos Tab */}
        {activeTab === 'photos' && (
          <div className="bg-white rounded-2xl p-5 shadow-lg">
            <h3 className="font-semibold text-slate-900 mb-4">Job Photos</h3>
            {job.photos.length === 0 ? (
              <div className="text-center py-8">
                <CameraIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">No photos available yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {job.photos.map((photo) => (
                  <a key={photo.id} href={photo.url} target="_blank" rel="noreferrer" className="aspect-square bg-slate-100 rounded-xl overflow-hidden relative">
                    <img src={photo.url} alt={photo.kind || 'Job photo'} className="w-full h-full object-cover" />
                    <div className="absolute bottom-0 inset-x-0 bg-black/40 text-white text-xs px-2 py-1">
                      {photo.kind}{photo.taken_at ? ` · ${new Date(photo.taken_at).toLocaleDateString()}` : ''}
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Price Tab */}
        {activeTab === 'invoice' && (
          <>
            <div className="bg-white rounded-2xl p-5 shadow-lg">
              <h3 className="font-semibold text-slate-900 mb-4">Cost Breakdown</h3>
              {priceQuote ? (
                <>
                  <div className="text-xs text-slate-500 mb-3">From quote v{priceQuote.version} ({priceQuote.status})</div>
                  <div className="space-y-2 mb-3">
                    {priceQuote.lines.map((l, i) => (
                      <div key={i} className="flex justify-between gap-3 text-sm">
                        <span className="text-slate-600">{l.description}</span>
                        <span className="font-medium text-slate-900 whitespace-nowrap">{money(l.total)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2 border-t border-slate-200 pt-3">
                    <div className="flex justify-between text-sm"><span className="text-slate-600">Labour</span><span>{money(priceQuote.labour_total)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-600">Materials</span><span>{money(priceQuote.materials_total)}</span></div>
                    {Number(priceQuote.other_total) > 0 && <div className="flex justify-between text-sm"><span className="text-slate-600">Other</span><span>{money(priceQuote.other_total)}</span></div>}
                    {Number(priceQuote.vat_pct) > 0 && <div className="flex justify-between text-sm"><span className="text-slate-600">VAT</span><span>{Number(priceQuote.vat_pct)}%</span></div>}
                    <div className="flex justify-between pt-2">
                      <span className="font-semibold text-slate-900">Quote total</span>
                      <span className="font-bold text-slate-900">{money(priceQuote.total)}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex justify-between">
                  <span className="font-semibold text-slate-900">{['COMPLETED', 'APPROVED'].includes(job.job_status) ? 'Job total' : 'Estimated total'}</span>
                  <span className="font-bold text-slate-900">{money(job.total_amount ?? job.estimated_budget)}</span>
                </div>
              )}
              {!['COMPLETED', 'APPROVED'].includes(job.job_status) && (
                <div className="mt-4 p-3 bg-yellow-50 rounded-xl">
                  <div className="flex items-start gap-2">
                    <AlertTriangleIcon className="text-yellow-600 mt-0.5" />
                    <div className="text-sm text-yellow-800">Approved changes to the work can change the final price.</div>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl p-5 shadow-lg">
              <h3 className="font-semibold text-slate-900 mb-3">Payment Status</h3>
              {job.invoice ? (
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-slate-900">Invoice {job.invoice.invoice_number}</div>
                    <div className="text-sm text-slate-600">
                      {money(job.invoice.total_amount)}
                      {job.invoice.paid_at ? ` · paid ${new Date(job.invoice.paid_at).toLocaleDateString()}` : job.invoice.due_date ? ` · due ${new Date(job.invoice.due_date).toLocaleDateString()}` : ''}
                      {job.invoice.payment_method ? ` · ${job.invoice.payment_method}` : ''}
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${job.invoice.paid_at ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {job.invoice.status}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-600">No invoice has been issued for this job yet.</div>
              )}
              <button disabled title="Not available yet"
                className="w-full mt-4 flex items-center justify-center gap-2 py-3 px-4 border border-slate-300 rounded-xl text-slate-700 font-medium opacity-50 cursor-not-allowed">
                <DownloadIcon />
                Download Invoice (not available yet)
              </button>
            </div>
          </>
        )}
      </div>

      {/* Report issue sheet */}
      {issueOpen && (
        <div className="fixed inset-0 z-30 bg-black/40 flex items-end" onClick={() => setIssueOpen(false)}>
          <form onSubmit={submitIssue} onClick={e => e.stopPropagation()} className="bg-white w-full rounded-t-2xl p-5 space-y-3 pb-24">
            <h3 className="font-semibold text-slate-900">Report an issue</h3>
            <select value={issue.type} onChange={e => setIssue({ ...issue, type: e.target.value })} className="w-full border border-slate-300 rounded-xl p-3 text-sm">
              {ISSUE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <textarea rows={4} required value={issue.description} onChange={e => setIssue({ ...issue, description: e.target.value })}
              placeholder="What went wrong?" className="w-full border border-slate-300 rounded-xl p-3 text-sm" />
            <div className="flex gap-2">
              <button type="button" onClick={() => setIssueOpen(false)} className="flex-1 py-3 border border-slate-300 rounded-xl font-medium">Cancel</button>
              <button type="submit" disabled={busy === 'issue'} className="flex-1 py-3 bg-slate-900 text-white rounded-xl font-medium disabled:opacity-50">
                {busy === 'issue' ? 'Sending…' : 'Send'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 safe-area-inset">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex gap-3">
            <button onClick={handleCall} disabled={!phone} title={phone ? undefined : 'No phone number on file'}
              className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-slate-300 rounded-xl text-slate-700 font-medium hover:bg-slate-50 touch-manipulation transition-colors disabled:opacity-40">
              <PhoneIcon />
              Call
            </button>
            <button onClick={handleMessage} disabled={!phone} title={phone ? undefined : 'No phone number on file'}
              className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-slate-300 rounded-xl text-slate-700 font-medium hover:bg-slate-50 touch-manipulation transition-colors disabled:opacity-40">
              <MessageCircleIcon />
              Message
            </button>
            <button
              onClick={() => { haptics.light(); setIssueOpen(true) }}
              className="flex-1 py-3 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 touch-manipulation transition-colors shadow-md"
            >
              Report Issue
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
